/** @jsxImportSource react */
import { useEffect, useMemo, useState } from 'react'
import {
  Alert, Autocomplete, Box, Button, Checkbox, Divider, FormControlLabel, IconButton, MenuItem, Paper, Stack, Table, TableBody, TableCell,
  TableHead, TableRow, TextField, Typography,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { processAPI } from '../../api/crm'
import { useEntityTypes, usePublishedForm, useTenantKey } from '../../hooks/useCrmQueries'
import { allFields } from '../../components/records/fieldValues'
import { assignable, useMembers } from '../../components/records/PersonInput'
import PageHeader from '../../components/common/PageHeader'
import { useNotify } from '../../components/common/NotificationProvider'
import { entityLabelPlural } from '../../utils/helpers'
import { getErrorList, getErrorMessage } from '../../utils/apiErrors'

const OPS = [['gt', 'more than'], ['gte', 'at least'], ['lt', 'less than'], ['lte', 'at most'], ['eq', 'is'], ['neq', 'is not']]
const EMPTY = { stageField: '', transitions: {}, checklists: {}, approvals: [] }

/** Admins: per record type, which stage moves are allowed, checklists, and approval rules. */
const ProcessSettingsPage = () => {
  const tenantId = useTenantKey()
  const queryClient = useQueryClient()
  const notify = useNotify()
  const { entityTypes, forms } = useEntityTypes()
  const { data: members = [] } = useMembers()
  const saved = useQuery({ queryKey: [tenantId, 'process', 'settings'], queryFn: processAPI.all })
  const [entityType, setEntityType] = useState('')
  const [v, setV] = useState(EMPTY)
  useEffect(() => { if (!entityType && entityTypes.length) setEntityType(entityTypes[0]) }, [entityType, entityTypes])
  useEffect(() => { setV(saved.data?.[entityType] || EMPTY) }, [saved.data, entityType])
  const { data: form } = usePublishedForm(forms.find((f) => f.entityType === entityType)?.code)
  const fields = useMemo(() => allFields(form), [form])
  const stage = fields.find((f) => f.fieldKey === v.stageField)
  const options = stage?.options || []
  const label = (x) => options.find((o) => o.value === x)?.label || x

  const save = useMutation({
    mutationFn: () => processAPI.save(entityType, {
      ...v,
      checklists: Object.fromEntries(Object.entries(v.checklists).map(([k, items]) => [k, items.map((x) => x.trim()).filter(Boolean)]).filter(([, items]) => items.length)),
    }),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: [tenantId, 'process'] }); notify('Process saved') },
  })
  const remove = useMutation({
    mutationFn: () => processAPI.remove(entityType),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: [tenantId, 'process'] }); notify('Process removed') },
  })
  const restricted = (from) => Object.prototype.hasOwnProperty.call(v.transitions, from)
  const toggleRow = (from, on) => {
    const t = { ...v.transitions }
    if (on) t[from] = options.map((o) => o.value).filter((x) => x !== from)
    else delete t[from]
    setV({ ...v, transitions: t })
  }
  const toggle = (from, to) => {
    const list = v.transitions[from] || []
    setV({ ...v, transitions: { ...v.transitions, [from]: list.includes(to) ? list.filter((x) => x !== to) : [...list, to] } })
  }
  const setRule = (i, patch) => setV({ ...v, approvals: v.approvals.map((r, j) => (j === i ? { ...r, ...patch } : r)) })
  const people = assignable(members)

  return (
    <Box sx={{ maxWidth: 980 }}>
      <PageHeader title="Process and approvals" subtitle="Guide records through their stages: allowed moves, checklists, and changes that need approval." />
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack spacing={2.5}>
          {(save.error || remove.error) && <Alert severity="error">{[getErrorMessage(save.error || remove.error), ...getErrorList(save.error)].join(' · ')}</Alert>}
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField select size="small" label="Record type" value={entityType} onChange={(e) => setEntityType(e.target.value)} sx={{ minWidth: 200 }}>
              {entityTypes.map((t) => <MenuItem key={t} value={t}>{entityLabelPlural(t)}{saved.data?.[t] ? ' · set up' : ''}</MenuItem>)}
            </TextField>
            <TextField select size="small" label="Stage field" value={v.stageField || ''} sx={{ minWidth: 200 }}
              onChange={(e) => setV({ ...EMPTY, stageField: e.target.value })}>
              {fields.filter((f) => ['SELECT', 'RADIO'].includes(f.fieldType)).map((f) => <MenuItem key={f.fieldKey} value={f.fieldKey}>{f.label}</MenuItem>)}
            </TextField>
          </Stack>
          {stage && (
            <>
              <Box>
                <Typography variant="subtitle2">Allowed moves</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>Tick “Limit” on a row to choose where records may go from that stage; other rows can go anywhere.</Typography>
                <Box sx={{ overflowX: 'auto' }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>From</TableCell><TableCell>Limit</TableCell>
                        {options.map((o) => <TableCell key={o.value} align="center">{o.label}</TableCell>)}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {[{ value: '', label: 'A new record' }, ...options].map((from) => (
                        <TableRow key={from.value}>
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>{from.label}</TableCell>
                          <TableCell><Checkbox size="small" checked={restricted(from.value)} onChange={(e) => toggleRow(from.value, e.target.checked)} /></TableCell>
                          {options.map((to) => (
                            <TableCell key={to.value} align="center">
                              {to.value !== from.value && restricted(from.value) && (
                                <Checkbox size="small" checked={(v.transitions[from.value] || []).includes(to.value)} onChange={() => toggle(from.value, to.value)} />
                              )}
                            </TableCell>
                          ))}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Box>
              </Box>
              <Divider />
              <Box>
                <Typography variant="subtitle2">Checklists</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>Things to tick on the record before it can leave a stage. One per line.</Typography>
                <Stack spacing={1.5}>
                  {options.map((o) => (
                    <TextField key={o.value} size="small" label={`Before leaving ${o.label}`} multiline minRows={1}
                      value={(v.checklists[o.value] || []).join('\n')}
                      onChange={(e) => {
                        const items = e.target.value.split('\n')
                        const c = { ...v.checklists }
                        if (items.every((x) => !x.trim())) delete c[o.value]
                        else c[o.value] = items
                        setV({ ...v, checklists: c })
                      }} />
                  ))}
                </Stack>
              </Box>
              <Divider />
              <Box>
                <Typography variant="subtitle2">Approvals</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>Moving into these stages waits for someone’s OK. Nobody chosen and no manager set: the admins approve.</Typography>
                <Stack spacing={1.5}>
                  {v.approvals.map((r, i) => (
                    <Paper key={r.id || i} variant="outlined" sx={{ p: 1.5 }}>
                      <Stack spacing={1.5}>
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                          <TextField size="small" label="Rule name" value={r.name || ''} onChange={(e) => setRule(i, { name: e.target.value })} sx={{ flex: 1 }} />
                          <Autocomplete multiple size="small" options={options.map((o) => o.value)} value={r.toStages || []} getOptionLabel={label} sx={{ flex: 1 }}
                            onChange={(_, toStages) => setRule(i, { toStages })} renderInput={(p) => <TextField {...p} label="Moving to" />} />
                          <IconButton aria-label="Remove rule" onClick={() => setV({ ...v, approvals: v.approvals.filter((_, j) => j !== i) })}><DeleteOutlineIcon /></IconButton>
                        </Stack>
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }}>
                          <Typography variant="body2" color="text.secondary">Only when</Typography>
                          <TextField select size="small" value={r.when?.field || ''} sx={{ minWidth: 160 }}
                            onChange={(e) => setRule(i, { when: e.target.value ? { field: e.target.value, op: r.when?.op || 'gt', value: r.when?.value || '' } : null })}>
                            <MenuItem value=""><em>always</em></MenuItem>
                            {fields.filter((f) => f.fieldKey !== v.stageField).map((f) => <MenuItem key={f.fieldKey} value={f.fieldKey}>{f.label}</MenuItem>)}
                          </TextField>
                          {r.when?.field && (
                            <>
                              <TextField select size="small" value={r.when.op} onChange={(e) => setRule(i, { when: { ...r.when, op: e.target.value } })}>
                                {OPS.map(([op, l]) => <MenuItem key={op} value={op}>{l}</MenuItem>)}
                              </TextField>
                              <TextField size="small" value={r.when.value} onChange={(e) => setRule(i, { when: { ...r.when, value: e.target.value } })} sx={{ width: 140 }} />
                            </>
                          )}
                        </Stack>
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }}>
                          <Autocomplete multiple size="small" options={people.map((m) => m.userId)} value={r.approverIds || []} sx={{ flex: 1 }}
                            getOptionLabel={(id) => { const m = members.find((x) => x.userId === id); return m?.name || m?.email || 'Member' }}
                            onChange={(_, approverIds) => setRule(i, { approverIds })} renderInput={(p) => <TextField {...p} label="Approvers" />} />
                          <FormControlLabel control={<Checkbox checked={!!r.requesterManager} onChange={(e) => setRule(i, { requesterManager: e.target.checked })} />}
                            label={<Typography variant="body2">and the asker’s manager</Typography>} />
                        </Stack>
                      </Stack>
                    </Paper>
                  ))}
                  <Box><Button startIcon={<AddIcon />} onClick={() => setV({ ...v, approvals: [...v.approvals, { name: '', toStages: [], when: null, approverIds: [], requesterManager: true }] })}>Add an approval rule</Button></Box>
                </Stack>
              </Box>
            </>
          )}
          <Stack direction="row" spacing={1} justifyContent="flex-end">
            {saved.data?.[entityType] && <Button color="error" disabled={remove.isPending} onClick={() => remove.mutate()}>Remove process</Button>}
            <Button variant="contained" disabled={!v.stageField || save.isPending} onClick={() => save.mutate()}>Save</Button>
          </Stack>
        </Stack>
      </Paper>
    </Box>
  )
}

export default ProcessSettingsPage
